import { parseCommercialInvoiceFile } from '../../api/parseCommercialInvoiceFile'
import type { UploadedCIMeta } from './types'

// Minimal shape the 6 checks actually read from — deliberately not the full
// commercial_invoices/invoice_items DB row shape, since this data now comes
// from an uploaded file, not a table.
export interface CIInvoice {
  id: string
  invoice_no: string | null
  job_ref: string | null
  shipper: string | null
  shipper_address: string | null
  consignee: string | null
  consignee_address: string | null
  incoterms: string | null
  packages: number | null
  gross_weight_kg: number | null
  // K10: null (not 0) when the document has no CBM column/total row at all.
  total_cbm: number | null
}

export interface CIItem {
  id: string
  invoice_id: string
  line_no: number | null
  hs_code: string | null
  goods_description: string | null
  pcs: number | null
  ctns: number | null
  gross_weight: number | null
  net_weight: number | null
  cbm: number | null
}

export interface CIVerificationData {
  invoices: CIInvoice[]
  items: CIItem[]
  // K10: cbm is null (not 0) when nothing on the CI captured a CBM figure at all --
  // distinguishes "not captured" from "genuinely zero" for the Totals check's N/A logic.
  totals: { packages: number; grossWeightKg: number; cbm: number | null }
}

async function hashFile(file: File): Promise<string> {
  const buf = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buf)
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('')
}

export async function buildCIVerificationDataFromUpload(
  file: File,
  jobRef: string
): Promise<{ ciData: CIVerificationData; meta: UploadedCIMeta }> {
  const [parsed, fileHash] = await Promise.all([parseCommercialInvoiceFile(file), hashFile(file)])

  const invoiceId = `upload-${fileHash.slice(0, 16)}`
  const invoice: CIInvoice = {
    id: invoiceId,
    invoice_no: parsed.header.invoice_no,
    job_ref: jobRef,
    shipper: parsed.header.shipper,
    shipper_address: parsed.header.shipper_address,
    consignee: parsed.header.consignee,
    consignee_address: parsed.header.consignee_address,
    incoterms: parsed.header.incoterms,
    packages: parsed.header.packages,
    gross_weight_kg: parsed.header.gross_weight_kg,
    total_cbm: parsed.header.total_cbm,
  }

  const items: CIItem[] = (parsed.items ?? []).map((item, idx) => ({
    id: `${invoiceId}-${idx}`,
    invoice_id: invoiceId,
    line_no: item.line_no,
    hs_code: item.hs_code,
    goods_description: item.goods_description,
    pcs: item.pcs,
    ctns: item.ctns,
    gross_weight: item.gross_weight,
    net_weight: item.net_weight,
    cbm: item.cbm,
  }))

  // K10: prefer the header/total-row CBM when present; fall back to summing item-level
  // CBM (only counts as "captured" if at least one item actually has a value) so a
  // document with per-line CBM but no total row still verifies rather than going N/A.
  const itemsWithCbm = items.filter(i => i.cbm != null)
  const ciCbm = invoice.total_cbm ?? (itemsWithCbm.length > 0
    ? itemsWithCbm.reduce((sum, i) => sum + (i.cbm ?? 0), 0)
    : null)

  const ciData: CIVerificationData = {
    invoices: [invoice],
    items,
    totals: {
      packages: invoice.packages ?? 0,
      // Z98: compare against the weight PRINTED on the invoice -- gross_weight_kg is now the sum of the 2 dp lines
      grossWeightKg: (invoice as any).stated_gross_weight_kg ?? invoice.gross_weight_kg ?? 0,
      cbm: ciCbm,
    },
  }

  const meta: UploadedCIMeta = {
    fileName: file.name,
    fileHash,
    storageKey: null,
    parsed,
  }

  return { ciData, meta }
}
